import { lazy, Suspense } from "react";
import Navigation from "@/components/cv/Navigation";
import HeroSection from "@/components/cv/HeroSection";
import { SectionSkeleton } from "@/components/ui/loading-skeleton";
import SkipLink from "@/components/ui/skip-link";
import { useLanguage } from "@/contexts/LanguageContext";

// Lazy load below-the-fold sections for better performance
const CompanyLogos = lazy(() => import("@/components/cv/CompanyLogos"));
const LuxuryValueProposition = lazy(() => import("@/components/cv/LuxuryValueProposition"));
const ToolsSection = lazy(() => import("@/components/cv/ToolsSection"));
const SkillsSection = lazy(() => import("@/components/cv/SkillsSection"));
const ExperienceSection = lazy(() => import("@/components/cv/ExperienceSection"));
const PortfolioSection = lazy(() => import("@/components/cv/PortfolioSection"));
const LuxuryFitScore = lazy(() => import("@/components/cv/LuxuryFitScore"));
const BookSection = lazy(() => import("@/components/cv/BookSection"));
const CertificationsSection = lazy(() => import("@/components/cv/CertificationsSection"));
const ContactSection = lazy(() => import("@/components/cv/ContactSection"));
const Footer = lazy(() => import("@/components/cv/Footer"));

const Index = () => {
  const { language } = useLanguage();
  const skipToContent = language === "fr" ? "Aller au contenu principal" : "Skip to main content";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Accessibility: Skip link for keyboard users */}
      <SkipLink href="#main-content">{skipToContent}</SkipLink>
      
      <Navigation />
      
      <main id="main-content" role="main" aria-label={language === "fr" ? "Contenu principal" : "Main content"}>
        {/* Hero loads immediately (above the fold) */}
        <HeroSection />
        
        {/* Company logos band */}
        <Suspense fallback={null}>
          <CompanyLogos />
        </Suspense>
        
        {/* Luxury Value Proposition - NEW */}
        <Suspense fallback={<SectionSkeleton />}>
          <LuxuryValueProposition />
        </Suspense>

        {/* Tools & Technologies Section */}
        <Suspense fallback={<SectionSkeleton />}>
          <ToolsSection />
        </Suspense>
        
        {/* Lazy loaded sections with loading fallback */}
        <Suspense fallback={<SectionSkeleton />}>
          <SkillsSection />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <ExperienceSection />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <PortfolioSection />
        </Suspense>
        
        {/* Luxury Fit Score - Enhanced */}
        <Suspense fallback={<SectionSkeleton />}>
          <LuxuryFitScore />
        </Suspense>
        
        {/* Book Section - NEW */}
        <Suspense fallback={<SectionSkeleton />}>
          <BookSection />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <CertificationsSection />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <ContactSection />
        </Suspense>
      </main>
      
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
